import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import StaffLayout from "@/components/StaffLayout";
import AdminLayout from "@/components/AdminLayout";
import Login from "@/pages/Login";
import StaffHome from "@/pages/staff/Home";
import StaffSetup from "@/pages/staff/Setup";
import ShiftRequest from "@/pages/staff/ShiftRequest";
import MyShifts from "@/pages/staff/MyShifts";
import StaffMessages from "@/pages/staff/Messages";
import ShiftCreation from "@/pages/manager/ShiftCreation";
import ConfirmedShifts from "@/pages/manager/ConfirmedShifts";
import RequiredStaff from "@/pages/manager/RequiredStaff";
import ManagerMessages from "@/pages/manager/Messages";
import ChangeRequests from "@/pages/manager/ChangeRequests";
import StaffStatus from "@/pages/manager/StaffStatus";
import StaffManagement from "@/pages/admin/StaffManagement";
import AssignmentManagement from "@/pages/admin/AssignmentManagement";
import LeadsManagement from "@/pages/admin/LeadsManagement";
import InquiriesManagement from "@/pages/admin/InquiriesManagement";
import CouponsManagement from "@/pages/admin/CouponsManagement";
import TenantsManagement from "@/pages/admin/TenantsManagement";
import AdminProfile from "@/pages/admin/AdminProfile";
import UserProfile from "@/pages/UserProfile";
import Landing from "@/pages/Landing";
import Help from "@/pages/Help";
import Flyer from "@/pages/Flyer";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

function StaffSetupGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  // If staff and profile not completed, force /staff/setup
  if (user?.role === "staff" && !user.profile_completed && location.pathname !== "/staff/setup") {
    return <Navigate to="/staff/setup" replace />;
  }
  return children;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">読み込み中...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "manager") return <Navigate to="/manager" replace />;
  return <Navigate to="/staff" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/admin-portal" element={<Login />} />
          <Route path="/:tenantSlug/login" element={<Login />} />

          {/* Staff setup page — outside of StaffLayout to hide bottom nav */}
          <Route
            path="/staff/setup"
            element={
              <ProtectedRoute roles={["staff"]}>
                <StaffSetup />
              </ProtectedRoute>
            }
          />

          {/* Staff routes */}
          <Route
            path="/staff"
            element={
              <ProtectedRoute roles={["staff"]}>
                <StaffSetupGate>
                  <StaffLayout />
                </StaffSetupGate>
              </ProtectedRoute>
            }
          >
            <Route index element={<StaffHome />} />
            <Route path="request" element={<ShiftRequest />} />
            <Route path="my-shifts" element={<MyShifts />} />
            <Route path="messages" element={<StaffMessages />} />
            <Route path="help" element={<Help />} />
          </Route>

          {/* Manager routes */}
          <Route
            path="/manager"
            element={
              <ProtectedRoute roles={["manager"]}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="shift-creation" replace />} />
            <Route path="shift-creation" element={<ShiftCreation />} />
            <Route path="staff-status" element={<StaffStatus />} />
            <Route path="confirmed" element={<ConfirmedShifts />} />
            <Route path="required-staff" element={<RequiredStaff />} />
            <Route path="messages" element={<ManagerMessages />} />
            <Route path="change-requests" element={<ChangeRequests />} />
            <Route path="staff" element={<StaffManagement />} />
            <Route path="assignments" element={<AssignmentManagement />} />
            <Route path="profile" element={<UserProfile />} />
            <Route path="help" element={<Help />} />
          </Route>

          {/* Admin routes — admin はリード管理専用 */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="leads" replace />} />
            <Route path="leads" element={<LeadsManagement />} />
            <Route path="inquiries" element={<InquiriesManagement />} />
            <Route path="coupons" element={<CouponsManagement />} />
            <Route path="tenants" element={<TenantsManagement />} />
            <Route path="profile" element={<AdminProfile />} />
            <Route path="help" element={<Help />} />
          </Route>

          <Route path="/" element={<Landing />} />
          <Route path="/campaigns/:slug" element={<Landing />} />
          <Route path="/maruhei" element={<Landing />} />
          <Route path="/flyer" element={<Flyer />} />
          <Route path="/flyer/:slug" element={<Flyer />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
